import { EmailTemplateEditor } from '@/components/forms/EmailTemplateEditor';
import { Button } from '@/components/ui/Button';
import {
  EmailTemplate,
  emailTemplatesService,
} from '@/services/emailTemplates.service';
import { useOrganisationsStore } from '@/store/organisations.store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ClipboardList,
  Edit,
  FileText,
  Mail,
  Plus,
  Send,
  Star,
  Trash2,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type TemplateType = 'dsn' | 'bulletins' | 'editions';

type TemplateEditorFormData = {
  name: string;
  subject: string;
  htmlContent: string;
  isActive: boolean;
  isDefault?: boolean;
};

type TemplateTypeConfig = {
  type: TemplateType;
  label: string;
  description: string;
  icon: LucideIcon;
  accent: string;
};

const templateTypeOrder: TemplateType[] = ['bulletins', 'dsn', 'editions'];

export function TemplatesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const orgUuid = useOrganisationsStore((s) => s.currentOrganisation?.uuid);
  const [selectedTemplate, setSelectedTemplate] =
    useState<EmailTemplate | null>(null);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [newTemplateType, setNewTemplateType] = useState<TemplateType | null>(
    null
  );

  const { data: allEmailTemplates, refetch: refetchTemplates } = useQuery({
    queryKey: ['email-templates-all', orgUuid],
    queryFn: () => emailTemplatesService.getAllTemplatesGrouped(),
  });

  const createTemplateMutation = useMutation({
    mutationFn: (
      templateData: TemplateEditorFormData & {
        templateType: TemplateType;
      }
    ) =>
      emailTemplatesService.createEmailTemplate({
        templateType: templateData.templateType,
        name: templateData.name,
        subject: templateData.subject,
        htmlContent: templateData.htmlContent,
        isActive: templateData.isActive,
        isDefault: templateData.isDefault,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates-all'] });
      refetchTemplates();
      setShowTemplateEditor(false);
      setSelectedTemplate(null);
      setNewTemplateType(null);
    },
  });

  const updateTemplateMutation = useMutation({
    mutationFn: ({
      templateUuid,
      templateData,
    }: {
      templateUuid: string;
      templateData: TemplateEditorFormData;
    }) => emailTemplatesService.updateEmailTemplate(templateUuid, templateData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates-all'] });
      refetchTemplates();
      setShowTemplateEditor(false);
      setSelectedTemplate(null);
      setNewTemplateType(null);
    },
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: (templateUuid: string) =>
      emailTemplatesService.deleteEmailTemplate(templateUuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates-all'] });
      refetchTemplates();
    },
  });

  const setDefaultTemplateMutation = useMutation({
    mutationFn: (templateUuid: string) =>
      emailTemplatesService.setAsDefault(templateUuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates-all'] });
      refetchTemplates();
    },
  });

  const templateTypes: TemplateTypeConfig[] = [
    {
      type: 'bulletins',
      label: t('templates.bulletins', 'Bulletins'),
      description: t(
        'templates.typeDescriptions.bulletins',
        'Message envoyé avec les bulletins de paie.'
      ),
      icon: FileText,
      accent: 'border-l-sky-500',
    },
    {
      type: 'dsn',
      label: t('templates.dsn', 'DSN'),
      description: t(
        'templates.typeDescriptions.dsn',
        'Message envoyé avec le récapitulatif DSN.'
      ),
      icon: Send,
      accent: 'border-l-primary-600',
    },
    {
      type: 'editions',
      label: t('templates.editions', 'Editions'),
      description: t(
        'templates.typeDescriptions.editions',
        'Message envoyé avec les éditions et exports.'
      ),
      icon: ClipboardList,
      accent: 'border-l-amber-500',
    },
  ];

  const templateTypeLabels = templateTypes.reduce(
    (labels, item) => ({ ...labels, [item.type]: item.label }),
    {} as Record<TemplateType, string>
  );

  const totalTemplates = templateTypeOrder.reduce(
    (total, type) => total + (allEmailTemplates?.[type]?.length || 0),
    0
  );

  const activeTemplates = templateTypeOrder.reduce(
    (total, type) =>
      total +
      (allEmailTemplates?.[type]?.filter(template => template.isActive)
        .length || 0),
    0
  );

  const handleTemplateSave = async (
    templateData: TemplateEditorFormData & {
      templateType?: TemplateType;
    }
  ) => {
    if (selectedTemplate) {
      await updateTemplateMutation.mutateAsync({
        templateUuid: selectedTemplate.uuid,
        templateData: {
          name: templateData.name,
          subject: templateData.subject,
          htmlContent: templateData.htmlContent,
          isActive: templateData.isActive,
          isDefault: templateData.isDefault,
        },
      });
      return;
    }

    if (!templateData.templateType) {
      throw new Error('Template type is required for new templates');
    }

    await createTemplateMutation.mutateAsync({
      templateType: templateData.templateType,
      name: templateData.name,
      subject: templateData.subject,
      htmlContent: templateData.htmlContent,
      isActive: templateData.isActive,
      isDefault: templateData.isDefault,
    });
  };

  const handleOpenTemplateEditor = async (
    template?: EmailTemplate,
    templateType?: TemplateType
  ) => {
    if (template) {
      try {
        const freshTemplate = await emailTemplatesService.getEmailTemplate(
          template.uuid
        );
        setSelectedTemplate(freshTemplate.data);
        setNewTemplateType(freshTemplate.data.templateType);
      } catch (error) {
        console.error('Erreur lors de la recuperation du template:', error);
        setSelectedTemplate(template);
        setNewTemplateType(template.templateType);
      }
    } else {
      setSelectedTemplate(null);
      setNewTemplateType(templateType || null);
    }

    setShowTemplateEditor(true);
  };

  const handleSetAsDefault = async (templateUuid: string) => {
    try {
      await setDefaultTemplateMutation.mutateAsync(templateUuid);
    } catch (error) {
      console.error('Error setting template as default:', error);
    }
  };

  const handleCloseEditor = () => {
    setShowTemplateEditor(false);
    setSelectedTemplate(null);
    setNewTemplateType(null);
  };

  const canOpenEditor = Boolean(selectedTemplate || newTemplateType);

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-5 border-b border-neutral-200 pb-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary-600">
            {t('templates.pageKicker', 'Messages envoyés')}
          </p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight text-neutral-950">
            {t('templates.pageTitle', 'Modèles email')}
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-neutral-600">
            {t(
              'templates.pageDescription',
              'Choisissez le style de message, puis personnalisez le sujet et le contenu avant les envois.'
            )}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <div className="min-w-28 rounded-lg border border-neutral-200 bg-white px-4 py-3">
            <p className="text-xs text-neutral-500">
              {t('templates.pageStats.total', 'Modèles')}
            </p>
            <p className="text-xl font-semibold text-neutral-950">
              {totalTemplates}
            </p>
          </div>
          <div className="min-w-28 rounded-lg border border-neutral-200 bg-white px-4 py-3">
            <p className="text-xs text-neutral-500">
              {t('templates.pageStats.active', 'Actifs')}
            </p>
            <p className="text-xl font-semibold text-neutral-950">
              {activeTemplates}
            </p>
          </div>
          <div className="min-w-28 rounded-lg border border-neutral-200 bg-white px-4 py-3">
            <p className="text-xs text-neutral-500">
              {t('templates.pageStats.styles', 'Styles')}
            </p>
            <p className="text-xl font-semibold text-neutral-950">
              {templateTypeOrder.length}
            </p>
          </div>
        </div>
      </div>

      <section className="space-y-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-neutral-950">
              {t('templates.pageChooseStyle', 'Choisir le style de message')}
            </h2>
            <p className="text-sm text-neutral-500">
              {t(
                'templates.pageChooseStyleHelp',
                "Rien n'est présélectionné : le modèle est créé seulement après ce choix."
              )}
            </p>
          </div>
        </div>

        <div className="grid gap-3 lg:grid-cols-3">
          {templateTypes.map(item => {
            const Icon = item.icon;
            const count = allEmailTemplates?.[item.type]?.length || 0;

            return (
              <button
                key={item.type}
                type="button"
                onClick={() => handleOpenTemplateEditor(undefined, item.type)}
                className={`group flex min-h-36 w-full flex-col justify-between rounded-lg border border-neutral-200 border-l-4 ${item.accent} bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-neutral-300 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-neutral-100 text-neutral-700 transition group-hover:bg-neutral-900 group-hover:text-white">
                      <Icon className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 className="text-base font-semibold text-neutral-950">
                        {item.label}
                      </h3>
                      <p className="mt-1 text-sm leading-5 text-neutral-500">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 flex items-center justify-between text-sm">
                  <span className="text-neutral-500">
                    {t('templates.pageTemplatesCount', '{{count}} modèle(s)', {
                      count,
                    })}
                  </span>
                  <span className="inline-flex items-center font-medium text-primary-700">
                    <Plus className="mr-1 h-4 w-4" />
                    {t('templates.create', 'Creer')}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      <section className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-neutral-950">
            {t('templates.pageLibrary', 'Bibliothèque')}
          </h2>
          <p className="text-sm text-neutral-500">
            {t(
              'templates.pageLibraryHelp',
              'Modifiez, archivez ou définissez le modèle utilisé par défaut pour chaque style.'
            )}
          </p>
        </div>

        <div className="space-y-4">
          {templateTypes.map(item => {
            const templates = allEmailTemplates?.[item.type] || [];
            const Icon = item.icon;

            return (
              <section
                key={item.type}
                className="rounded-lg border border-neutral-200 bg-white"
              >
                <div className="flex flex-col gap-3 border-b border-neutral-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-neutral-100 text-neutral-700">
                      <Icon className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 className="text-sm font-semibold text-neutral-950">
                        {item.label}
                      </h3>
                      <p className="text-xs text-neutral-500">
                        {t('templates.pageTemplatesCount', '{{count}} modèle(s)', {
                          count: templates.length,
                        })}
                      </p>
                    </div>
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      handleOpenTemplateEditor(undefined, item.type)
                    }
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {t('templates.pageCreateFor', 'Créer {{type}}', {
                      type: item.label,
                    })}
                  </Button>
                </div>

                <div className="divide-y divide-neutral-100">
                  {templates.length > 0 ? (
                    templates.map(template => (
                      <div
                        key={template.uuid}
                        className="flex flex-col gap-4 px-5 py-4 transition hover:bg-neutral-50 sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h4 className="truncate text-sm font-medium text-neutral-950">
                              {template.name}
                            </h4>
                            {template.isActive && (
                              <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                                {t('common.active', 'Actif')}
                              </span>
                            )}
                            {template.isDefault && (
                              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                                {t('templates.pageDefault', 'Défaut')}
                              </span>
                            )}
                          </div>
                          <p className="mt-1 truncate text-sm text-neutral-600">
                            {template.subject}
                          </p>
                          <p className="mt-1 text-xs text-neutral-400">
                            {t('templates.pageCreated', 'Créé le {{date}}', {
                              date: new Date(
                                template.createdAt
                              ).toLocaleDateString('fr-FR'),
                            })}
                          </p>
                        </div>

                        <div className="flex shrink-0 items-center gap-2">
                          {!template.isDefault && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSetAsDefault(template.uuid)}
                              disabled={setDefaultTemplateMutation.isPending}
                              title={t(
                                'templates.setAsDefault',
                                'Définir comme défaut'
                              )}
                            >
                              <Star className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenTemplateEditor(template)}
                            title={t('common.edit', 'Modifier')}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              deleteTemplateMutation.mutate(template.uuid)
                            }
                            disabled={
                              deleteTemplateMutation.isPending ||
                              template.isDefault
                            }
                            title={t('common.delete', 'Supprimer')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="px-5 py-8 text-center">
                      <Mail className="mx-auto h-8 w-8 text-neutral-300" />
                      <h4 className="mt-3 text-sm font-medium text-neutral-950">
                        {t(
                          'templates.pageNoTemplatesForType',
                          'Aucun modèle {{type}}',
                          {
                            type: templateTypeLabels[item.type].toLowerCase(),
                          }
                        )}
                      </h4>
                      <p className="mx-auto mt-1 max-w-sm text-sm text-neutral-500">
                        {t(
                          'templates.pageEmptyTypeHelp',
                          'Choisissez ce style pour créer un modèle adapté.'
                        )}
                      </p>
                    </div>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      </section>

      {showTemplateEditor && canOpenEditor && (
        <EmailTemplateEditor
          template={selectedTemplate}
          templateType={(selectedTemplate?.templateType || newTemplateType) as TemplateType}
          folderUuid=""
          isOpen={showTemplateEditor}
          onClose={handleCloseEditor}
          onSave={handleTemplateSave}
          defaultTemplate={null}
        />
      )}
    </div>
  );
}
